import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, AlertTriangle, CheckCircle2, XCircle, Clock, Search, Filter, ChevronRight, Shield } from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

const KybSessions = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });

  useEffect(() => { fetchSessions(); fetchStats(); }, [filter]);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const params = { page: pagination.page, limit: 20 };
      if (filter !== 'ALL') params.status = filter;
      if (search) params.company_name = search;
      const res = await api.get('/kyb-levels/sessions', { params });
      setSessions(res.data.sessions || []);
      setPagination(res.data.pagination || { page: 1, pages: 1, total: 0 });
    } catch (error) {
      console.error('Error fetching sessions:', error);
      toast.error('Failed to load KYB sessions');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await api.get('/kyb-levels/sessions/stats/summary');
      setStats(res.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchSessions();
  };

  const getStatusBadge = (status) => {
    const badges = {
      NOT_STARTED: { icon: Clock, color: 'bg-gray-100 text-gray-800', label: 'Not Started' },
      IN_PROGRESS: { icon: Clock, color: 'bg-blue-100 text-blue-800', label: 'In Progress' },
      PENDING_REVIEW: { icon: AlertTriangle, color: 'bg-yellow-100 text-yellow-800', label: 'Pending Review' },
      APPROVED: { icon: CheckCircle2, color: 'bg-green-100 text-green-800', label: 'Approved' },
      APPROVED_WITH_CONTROLS: { icon: Shield, color: 'bg-orange-100 text-orange-800', label: 'Approved w/ Controls' },
      REJECTED: { icon: XCircle, color: 'bg-red-100 text-red-800', label: 'Rejected' },
      EXPIRED: { icon: Clock, color: 'bg-gray-100 text-gray-800', label: 'Expired' }
    };
    return badges[status] || badges.NOT_STARTED;
  };

  const getRiskBadge = (tier) => {
    const badges = {
      LOW: 'bg-green-100 text-green-800',
      MEDIUM: 'bg-yellow-100 text-yellow-800',
      HIGH: 'bg-red-100 text-red-800',
      PROHIBITED: 'bg-purple-100 text-purple-800'
    };
    return badges[tier] || 'bg-gray-100 text-gray-800';
  };

  const filterTabs = [
    { key: 'ALL', label: 'All Sessions', count: stats?.by_status ? Object.values(stats.by_status).reduce((a, b) => a + b, 0) : 0 },
    { key: 'PENDING_REVIEW', label: 'Needs Review', count: stats?.pending_review || 0 },
    { key: 'IN_PROGRESS', label: 'In Progress', count: stats?.by_status?.IN_PROGRESS || 0 },
    { key: 'APPROVED', label: 'Approved', count: (stats?.by_status?.APPROVED || 0) + (stats?.by_status?.APPROVED_WITH_CONTROLS || 0) },
    { key: 'REJECTED', label: 'Rejected', count: stats?.by_status?.REJECTED || 0 }
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Building2 className="w-6 h-6 text-blue-600" /> KYB Verification Sessions
        </h1>
        <p className="text-gray-600 mt-1">Review and manage business verification sessions</p>
      </div>

      {/* Stats Cards */}
      {stats?.high_risk_pending > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600" />
          <span className="text-red-800 font-medium">{stats.high_risk_pending} high-risk session(s) pending review</span>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="border-b border-gray-200 px-4">
          <nav className="flex gap-4 -mb-px">
            {filterTabs.map(tab => (
              <button key={tab.key} onClick={() => setFilter(tab.key)}
                className={`py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                  filter === tab.key ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}>
                {tab.label} {tab.count > 0 && <span className="ml-1 px-2 py-0.5 rounded-full bg-gray-100 text-xs">{tab.count}</span>}
              </button>
            ))}
          </nav>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-gray-200">
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by company name..." className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Search</button>
          </form>
        </div>

        {/* Sessions Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Risk</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Level</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr><td colSpan="6" className="px-6 py-12 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div></td></tr>
              ) : sessions.length === 0 ? (
                <tr><td colSpan="6" className="px-6 py-12 text-center text-gray-500">No sessions found</td></tr>
              ) : sessions.map((session) => {
                const status = getStatusBadge(session.status);
                const StatusIcon = status.icon;
                return (
                  <tr key={session.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/kyb-sessions/${session.id}`)}>
                    <td className="px-6 py-4"><div className="font-medium text-gray-900">{session.company_name}</div><div className="text-sm text-gray-500">{session.company_number}</div></td>
                    <td className="px-6 py-4"><span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${status.color}`}><StatusIcon className="w-3 h-3" />{status.label}</span></td>
                    <td className="px-6 py-4">{session.risk_tier && <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getRiskBadge(session.risk_tier)}`}>{session.risk_tier} ({session.risk_score})</span>}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{session.level?.name || '-'}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{new Date(session.created_at).toLocaleDateString()}</td>
                    <td className="px-6 py-4 text-right"><ChevronRight className="w-5 h-5 text-gray-400 inline" /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default KybSessions;

